<template>
  <div class="pe-option-container">

    <div>
      <p :class="[globalConfig.classes?.label]">{{availableOptions.filter(o => o.show_title).map(o =>
        `${o.title}`).join(', ')}}</p>
    </div>

    <div class="pe-drop-zone" :class="{ 'pe-dragging': isDragging, 'pe-disabled': availableOptions.length === 0 }"
      @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop"
      @click="triggerInput">
      <input ref="fileInput" type="file" multiple class="pe-hidden-input" @change="handleInputChange"
        :disabled="availableOptions.length === 0" :accept="computedAccept" />

      <div class="pe-drop-content">
        <svg xmlns="http://www.w3.org/2000/svg" width="37" height="32" viewBox="0 0 37 32" fill="none">
          <path
            d="M31.9615 1H5.03846C2.80808 1 1 2.83156 1 5.09091V26.9091C1 29.1684 2.80808 31 5.03846 31H31.9615C34.1919 31 36 29.1684 36 26.9091V5.09091C36 2.83156 34.1919 1 31.9615 1Z"
            stroke="var(--pe-primary-color, green)" stroke-width="2" stroke-linejoin="round" />
          <path
            d="M25.2309 11.9096C26.7178 11.9096 27.9232 10.6886 27.9232 9.18235C27.9232 7.67612 26.7178 6.45508 25.2309 6.45508C23.744 6.45508 22.5386 7.67612 22.5386 9.18235C22.5386 10.6886 23.744 11.9096 25.2309 11.9096Z"
            stroke="var(--pe-primary-color, green)" stroke-width="2" stroke-miterlimit="10" />
          <path
            d="M22.5385 22.7999L14.9108 15.0876C14.4254 14.5961 13.7731 14.3109 13.087 14.2903C12.401 14.2697 11.7331 14.5153 11.2198 14.9768L1 24.1814"
            stroke="var(--pe-primary-color, green)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          <path
            d="M15.8076 31.0002L26.1848 20.4882C26.6594 20.0065 27.2946 19.722 27.9657 19.6904C28.6368 19.6589 29.2953 19.8827 29.8118 20.3178L35.9999 25.5456"
            stroke="var(--pe-primary-color, green)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>

        <template v-if="availableOptions.length > 0">
          <div class="pe-inner-drop-content">
            <p class="main-text" :class="[globalConfig.classes?.title]">
              {{ isDragging ? 'Soltalo acá' : 'Cargá tus archivos' }}
            </p>
            <p class="sub-text" :class="[globalConfig.classes?.label]">
              Arrastrá y soltá o hacé clic
            </p>
            <p class="info-text" :class="[globalConfig.classes?.label]">
              (Queda{{ availableOptions.length > 1 ? 'n' : '' }} {{ availableOptions.length }} lugar{{
                availableOptions.length > 1 ? 'es' : '' }})
            </p>
          </div>
        </template>
        <template v-else>
          <div class="pe-inner-drop-content">
            <p class="main-text" :class="[globalConfig.classes?.title]">Completo</p>
            <p class="sub-text" :class="[globalConfig.classes?.label]">No puedes subir más archivos</p>
          </div>
        </template>
      </div>
    </div>

    <div v-if="localErrors.length > 0" class="pe-error-container">
      <div v-for="(err, index) in localErrors" :key="index" class="pe-toast-error" :class="[err.type, globalConfig.classes?.input]">
        {{ err.message }}
      </div>
    </div>

    <div v-if="filledOptions.length > 0" class="pe-ListOptions">
      <div v-for="opt in filledOptions" :key="opt.id" class="pe-ListItem">

        <div class="pe-file-info" :class="[globalConfig.classes?.input]">
          <template v-if="opt.isLoading">
            <span class="spinner"></span>
            <span class="uploading-text">Subiendo...</span>
          </template>

          <template v-else>
            <a :href="opt.value" target="_blank" class="pe-file-link" @click.stop>
              {{ getFileName(opt.value) }}
            </a>
          </template>
        </div>

        <button v-if="!opt.isLoading" class="pe-delete-btn" @click="removeFile(opt.id)" title="Eliminar archivo">
          ✕
        </button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue';

const props = defineProps({
  question: { type: Object, required: true },
});

const engine = inject('formEngine');
const globalConfig = inject('engineConfig');

const fileInput = ref(null);
const isDragging = ref(false);
const localErrors = ref([]); // Para mostrar toasts

// --- COMPUTADAS ---

// Opciones del grupo 'file'
const fileOptions = computed(() => props.question.optionGroups['file'] || []);

// Opciones vacías (Slots disponibles)
const availableOptions = computed(() => fileOptions.value.filter(o => !o.value && !o.isLoading));

// Opciones llenas (Para mostrar en lista)
const filledOptions = computed(() => fileOptions.value.filter(o => o.value || o.isLoading));

// Calcular el 'accept' del input basado en la primera opción disponible
const computedAccept = computed(() => {
  const extensions = availableOptions.value
    .flatMap(option => option.validation_rules?.file_extensions ?? [])
    .map(ext => ext.toLowerCase().replace(/^\./, '')); // normaliza

  if (extensions.length === 0) return '*';

  return [...new Set(extensions)].map(ext => `.${ext}`).join(',');
});

// --- MÉTODOS ---

const triggerInput = () => {
  if (availableOptions.value.length > 0) {
    fileInput.value.click();
  }
};

const handleInputChange = (e) => {
  const files = Array.from(e.target.files || []);
  processFiles(files);
  e.target.value = ''; // Reset para permitir subir el mismo archivo si falla
};

const handleDrop = (e) => {
  isDragging.value = false;
  const files = Array.from(e.dataTransfer.files || []);
  processFiles(files);
};

const showToast = (msg, type) => {
  localErrors.value.push({ message: msg, type });
  setTimeout(() => {
    localErrors.value.shift(); // Eliminar el mensaje más viejo después de 3s
  }, 3000);
};

const validateFile = (file, rules) => {
  if (!rules) return true;

  // 1. Tamaño
  if (rules.max_file_size) {
    // max_file_size suele venir en KB o MB, asumamos Bytes o KB según tu DB.
    // Estandar: si el número es bajo (< 100), son MB. Si es alto, KB o Bytes.
    // Ajusta esta lógica según tu backend. Asumiré que viene en MB para el ejemplo.
    const maxBytes = rules.max_file_size * 1024 * 1024;
    if (file.size > maxBytes) {
      showToast(`"${file.name}" es muy pesado. Máx: ${rules.max_file_size}MB`);
      return false;
    }
  }

  // 2. Extensión
  if (rules.file_extensions && rules.file_extensions.length > 0) {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!rules.file_extensions.map(e => e.toLowerCase()).includes(ext)) {
      showToast(`"${file.name}" tiene formato inválido. Aceptados: ${rules.file_extensions.join(', ')}`);
      return false;
    }
  }

  return true;
};

const processFiles = (files) => {
  if (files.length === 0) return;

  const availableSlots = fileOptions.value.filter(o => !o.value && !o.isLoading);

  files.forEach((file, index) => {

    if (index >= availableSlots.length) {
      showToast(`No hay más espacio. Archivo "${file.name}" ignorado.`, 'warning');
      return;
    }

    const targetOpt = availableSlots[index];

    if (!validateFile(file, targetOpt.validation_rules)) {
       showToast(`El archivo ${file.name} no es válido`, 'error');
       return;
    }

    // Llamamos al engine y esperamos su respuesta (sin await para que sea paralelo)
    engine.handleFileUpload(props.question.id, targetOpt.id, file)
      .then((error) => {
        if (error) {
          showToast(`Error con ${file.name}: ${error}`, 'error');
        }
      });
  });
};

const removeFile = (optionId) => {
  // Seteamos a null usando la updateFn estándar
  engine.setAnswer(props.question?.id, optionId, null)
};

// Helper visual para sacar nombre de la URL
const getFileName = (url) => {
  if (!url) return '';
  // Intenta decodificar la URL o sacar el último segmento
  try {
    return decodeURIComponent(url.split('/').pop());
  } catch (e) {
    return 'Archivo adjunto';
  }
};
</script>

<style scoped>
/* --- DROP ZONE --- */
.pe-drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  overflow: hidden;

  width: fit-content;

  border: var(--pe-drop-zone-border, 2px dashed #ccc);
  border-radius: var(--pe-drop-zone-border-radius, 0.5rem);
  background: var(--pe-drop-zone-bg, #fafafa);

  cursor: pointer;
  transition: all 0.2s ease;
  padding: 10px;
}

.pe-drop-zone:hover:not(.pe-disabled) {
  border-color: var(--pe-primary-color, green);
  background: var(--pe-translucent,rgba(0, 195, 168, 0.05));
}

.pe-drop-zone.pe-dragging {
  border-color: var(--pe-primary-color, green);
  background: var(--pe-translucent,rgba(0, 195, 168, 0.05));
  transform: scale(1.02);
}

.pe-drop-zone.pe-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  /* border-color: #eee; */
}

.pe-hidden-input {
  display: none;
}

.pe-drop-content {
  pointer-events: none;
  display: flex;
  flex-direction: row;
  gap: var(--pe-drop-content-gap, 1vw);
  align-items: center;
}

.pe-inner-drop-content {
  display: flex;
  flex-direction: column;
  gap: var(--pe-drop-content-gap, 0.4vw);
  text-align: left;
}

.pe-icon {
  display: block;
}

.main-text {
  color: var(--pe-input-text-color, #333);
}

.sub-text {
  color: var(--pe-disabled-text-color, #888);
}

.info-text {
  color: var(--pe-primary-color, green);
}

/* --- FILE LIST --- */
.pe-ListOptions {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  /* Importante por si son muchos */
  width: 100%;
  gap: var(--pe-qo-select-list-gap, 1.5vh);
  margin: var(--pe-qo-select-list-margin, 0);
}

.pe-ListItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  background: var(--pe-qo-select-list-item-bg-color,  #f5f5f5);
  border-radius: var(--pe-input-border-radius, 0.5rem);
  /* Un poco más standard */
  padding: var(--pe-qo-select-list-item-padding, 0.75vw);
  cursor: pointer;
  gap: var(--pe-options-gp, 1.5vh);

  background: var(--pe-selected-item-bg, #F5F5F5);
  /* box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); */
  transition: background 0.2s;
  width: fit-content;
}

.pe-file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.pe-file-link {
  color:  var(--pe-input-text-color, #333);
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: var(--pe-file-link-max-width, 200px);
}

.pe-file-link:hover {
  text-decoration: underline;
  color: var(--pe-primary-color, green);
}

.pe-delete-btn {
  font-weight: bold;
  color: #666;
  border: none;
  background: transparent;
}

.pe-delete-btn:hover {
  color: var(--pe-error-color, #ff4d4f);
}

/* --- LOADING SPINNER --- */
.spinner {
  width: var(--pe-loading-spinner-with, 1rem);
  aspect-ratio: 1/1;
  border: var(--pe-loading-spinner-border, 2px solid #eee);
  border-top-color: var(--pe-primary-color, green);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.uploading-text {
  color: #888;
  font-style: italic;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* --- TOAST ERROR --- */
.pe-error-container {
  display: flex;
  flex-direction: column;
  gap: var(--pe-toast-error-gap, 0.5rem);
}

.pe-toast-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  border-radius: var(--pe-input-border-radius, 0.5rem);
  padding: var(--pe-qo-select-error-item-padding, 0.75vw);
  cursor: pointer;
  gap: var(--pe-options-gp, 1.5vh);

  transition: background 0.2s;
  width: fit-content;
}

.pe-toast-error.error {
  background: var(--pe-error-item-bg, rgba(211, 47, 47, 0.2));
  color: var(--pe-error-item-color, #333);
  /* color: var(--pe-error-item-color, white); */
}

.pe-toast-error.warning {
  background: var(--pe-warning-item-bg, rgba(255, 185, 0, 0.2));
  color: var(--pe-warning-item-color, #333);
  /* color: var(--pe-warning-item-color, white); */
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>